import React from "react";
import { Box, Skeleton } from "@mui/material";

import ConversationListPane from "./panes/ConversationListPane";
import MessageHistoryPane from "./panes/MessageHistoryPane";
import ConversationDetailsPane from "./panes/ConversationDetailsPane";
import AppBootstrapErrorState from "../../components/AppBootstrapErrorState";
import { PERSONAL_WHATSAPP_CONVERSATION_PREFIX } from "../../api/client";

const ChatLayout = ({
  isMobile,
  containerStyle,
  userData,
  selectedConversationId,
  selectedConversation,
  showConversationList,
  showMessageHistory,
  showConversationDetails,
  isLoadingDetails,
  detailsAccessDenied = false,
  detailsDenied = null,
  initialLoading,
  isLoadingConversation,
  messagesSyncError,
  reloadConversationMessages,
  messages,
  conversations,
  conversationsTimeoutMessage,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isFetching,
  refetchConversations,
  searchTerm,
  setSearchTerm,
  filters,
  setFilters,
  advancedFilterState,
  applyChatFilterState,
  clearAdvancedFilterState,
  onSelectConversation,
  onSendMessage,
  onEndConversation,
  onUpdateConversation,
  onBack,
  onShowDetails,
  onOpenPopup,
  onCloseConversationList,
  onNewConversation,
  isCreatingConversation,
  isError,
  conversationsError,
  aiInsights,
  isLoadingInsights,
  aiInsightsError,
  onLoadAiInsights,
  hasMoreMessages,
  isLoadingMoreMessages,
  onLoadMoreMessages,
  onRefetchConversationDetails,
  onDeleteMessage,
  onEditNote,
  onDeleteNote,
  onDeletePersonalConversation,
  totalInDatabase,
  workspaceEmbedded = false,
  detailsPlacement = "right",
  popupCallMode = false,
}) => {
  const isPersonalWhatsappThread =
    Boolean(selectedConversation) &&
    (selectedConversation.kind === "personal" ||
      String(selectedConversation.id || "").startsWith(PERSONAL_WHATSAPP_CONVERSATION_PREFIX));
  const effectiveShowConversationDetails =
    Boolean(
      showConversationDetails &&
        selectedConversation &&
        !isPersonalWhatsappThread &&
        !detailsAccessDenied
    );
  const showDetailsOnLeft = detailsPlacement === "left" && effectiveShowConversationDetails;

  const detailsPane = effectiveShowConversationDetails && selectedConversation ? (
    <ConversationDetailsPane
      isMobile={isMobile}
      containerStyle={containerStyle}
      selectedConversation={selectedConversation}
      messages={messages}
      onClose={onShowDetails}
      onUpdateConversation={onUpdateConversation}
      aiInsights={aiInsights}
      isLoadingInsights={isLoadingInsights}
      aiInsightsError={aiInsightsError}
      onLoadAiInsights={onLoadAiInsights}
      onRefetchConversationDetails={onRefetchConversationDetails}
      reloadConversationMessages={reloadConversationMessages}
      hideCloseButton={popupCallMode}
    />
  ) : null;

  if (initialLoading) {
    return (
      <Box display="flex" gap={1} height="98%" p={0}>
        <Skeleton variant="rectangular" width="25%" height="100%" sx={{ minWidth: 200, borderRadius: 1 }} />
        <Skeleton variant="rectangular" height="100%" sx={{ flex: 1, borderRadius: 1 }} />
      </Box>
    );
  }

  if (isError) {
    const offlineFallback =
      typeof navigator !== "undefined" && navigator.onLine === false
        ? { code: "ERR_NETWORK", message: "Network Error" }
        : null;
    return (
      <AppBootstrapErrorState
        error={conversationsError || offlineFallback}
        onRetry={() => {
          if (typeof refetchConversations === "function") refetchConversations();
        }}
        isRetrying={Boolean(isFetching)}
        homeTo="/chat"
      />
    );
  }

  return (
    <Box
      className="tour-chat-layout"
      display="flex"
      p={0}
      gap={workspaceEmbedded ? 0 : 1}
      sx={{
        flex: 1,
        width: "100%",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {showConversationList && (
        <ConversationListPane
          isMobile={isMobile}
          containerStyle={containerStyle}
          workspaceEmbedded={workspaceEmbedded}
          conversations={conversations}
          conversationsTimeoutMessage={conversationsTimeoutMessage}
          selectedConversation={selectedConversation}
          onSelectConversation={onSelectConversation}
          fetchNextPage={fetchNextPage}
          refetchConversations={refetchConversations}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          isFetching={isFetching}
          initialLoading={initialLoading}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          filters={filters}
          setFilters={setFilters}
          advancedFilterState={advancedFilterState}
          applyChatFilterState={applyChatFilterState}
          clearAdvancedFilterState={clearAdvancedFilterState}
          isLoadingDetails={isLoadingDetails}
          onClose={onCloseConversationList}
          onNewConversation={onNewConversation}
          isCreatingConversation={isCreatingConversation}
        />
      )}

      {showDetailsOnLeft ? detailsPane : null}

      {showMessageHistory && (
        <MessageHistoryPane
          isMobile={isMobile}
          containerStyle={containerStyle}
          selectedConversationId={selectedConversationId}
          selectedConversation={selectedConversation}
          showConversationList={showConversationList}
          showConversationDetails={effectiveShowConversationDetails}
          userData={userData}
          isLoadingDetails={isLoadingDetails}
          detailsAccessDenied={detailsAccessDenied}
          detailsDenied={detailsDenied}
          initialLoading={initialLoading}
          isLoadingConversation={isLoadingConversation}
          messagesSyncError={messagesSyncError}
          reloadConversationMessages={reloadConversationMessages}
          messages={messages}
          onSendMessage={onSendMessage}
          onEndConversation={onEndConversation}
          onOpenPopup={onOpenPopup}
          onBack={onBack}
          onShowDetails={onShowDetails}
          onUpdateConversation={onUpdateConversation}
          onNewConversation={onNewConversation}
          onActivateConversation={onSelectConversation}
          hasMoreMessages={hasMoreMessages}
          isLoadingMoreMessages={isLoadingMoreMessages}
          onLoadMoreMessages={onLoadMoreMessages}
          onRefetchConversationDetails={onRefetchConversationDetails}
          onDeleteMessage={onDeleteMessage}
          onEditNote={onEditNote}
          onDeleteNote={onDeleteNote}
          onDeletePersonalConversation={onDeletePersonalConversation}
          queueConversations={conversations}
          queueFetchNextPage={fetchNextPage}
          queueHasNextPage={hasNextPage}
          queueIsFetchingNextPage={isFetchingNextPage}
          queueTotalCount={totalInDatabase}
          hideListToggle={popupCallMode}
          hideOpenPopupButton={popupCallMode}
          hideDetailsToggle={popupCallMode}
          popupCallMode={popupCallMode}
        />
      )}

      {!showDetailsOnLeft ? detailsPane : null}
    </Box>
  );
};

export default ChatLayout;
